import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import * as pdfjsLib from 'pdfjs-dist'
import { api } from '../lib/api'
import type { Document, SignatureField } from '../types'

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString()

const SCALE = 1.5

const FIELD_COLORS: Record<string, { bg: string; border: string }> = {
  SIGNATURE:   { bg: 'rgba(255,91,46,0.2)',   border: '#FF5B2E' },
  RUBRIC:      { bg: 'rgba(255,91,46,0.15)',  border: '#FF5B2E' },
  NAME:        { bg: 'rgba(59,130,246,0.2)',  border: '#3B82F6' },
  NATIONAL_ID: { bg: 'rgba(16,185,129,0.2)', border: '#10B981' },
  DATE:        { bg: 'rgba(139,92,246,0.2)',  border: '#8B5CF6' },
  TEXT:        { bg: 'rgba(245,158,11,0.2)',  border: '#F59E0B' },
  CHECKBOX:    { bg: 'rgba(99,102,241,0.2)',  border: '#6366F1' },
}

const FIELD_DEFAULTS: Record<string, { width: number; height: number }> = {
  SIGNATURE:   { width: 200, height: 60 },
  RUBRIC:      { width: 80,  height: 40 },
  NAME:        { width: 180, height: 24 },
  NATIONAL_ID: { width: 140, height: 24 },
  DATE:        { width: 100, height: 24 },
  TEXT:        { width: 160, height: 24 },
  CHECKBOX:    { width: 20,  height: 20 },
}

const FIELD_TYPES: Array<{ tipo: SignatureField['tipo']; label: string }> = [
  { tipo: 'SIGNATURE',   label: 'Assinatura' },
  { tipo: 'RUBRIC',      label: 'Rubrica' },
  { tipo: 'NAME',        label: 'Nome' },
  { tipo: 'NATIONAL_ID', label: 'CPF / ID' },
  { tipo: 'DATE',        label: 'Data' },
  { tipo: 'TEXT',        label: 'Texto' },
]

interface FieldForm {
  tipo: SignatureField['tipo']
  signerId: string
  page: number
  x: number
  y: number
  width: number
  height: number
  required: boolean
}

interface PageInfo {
  canvas: HTMLCanvasElement
  width: number   // PDF points (scale 1.0)
  height: number
}

export default function DocumentEditor() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [doc, setDoc] = useState<Document | null>(null)
  const [fields, setFields] = useState<FieldForm[]>([])
  const [pages, setPages] = useState<PageInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Active placement tool: which field type to place on next click
  const [activeTool, setActiveTool] = useState<SignatureField['tipo'] | null>(null)
  const [activeSignerId, setActiveSignerId] = useState('')
  const [selectedFieldIdx, setSelectedFieldIdx] = useState<number | null>(null)

  // Drag / resize state (uses refs to avoid re-renders during mouse move)
  const dragRef = useRef<{
    type: 'move' | 'resize'
    fieldIdx: number
    startMouseX: number
    startMouseY: number
    startFieldX: number
    startFieldY: number
    startW: number
    startH: number
  } | null>(null)

  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([])

  // Load document + PDF
  useEffect(() => {
    async function load() {
      try {
        const data = await api.get<Document>(`/api/documents/${id}`)
        setDoc(data)
        if (data.signatureFields?.length) {
          setFields(data.signatureFields.map((f) => ({
            tipo: f.tipo,
            signerId: f.signerId || '',
            page: f.page,
            x: f.x,
            y: f.y,
            width: f.width,
            height: f.height,
            required: f.required,
          })))
        }
        // Fetch PDF bytes
        const token = localStorage.getItem('pakt_token')
        const res = await fetch(`/api/documents/${id}/download`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        if (!res.ok) { setError(`Erro ao carregar PDF (${res.status})`); return }
        const arrayBuffer = await res.arrayBuffer()
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
        const rendered: PageInfo[] = []
        for (let p = 1; p <= pdf.numPages; p++) {
          const page = await pdf.getPage(p)
          const viewport = page.getViewport({ scale: SCALE })
          const canvas = document.createElement('canvas')
          canvas.width = viewport.width
          canvas.height = viewport.height
          await page.render({ canvasContext: canvas.getContext('2d')!, viewport }).promise
          rendered.push({ canvas, width: viewport.width / SCALE, height: viewport.height / SCALE })
        }
        setPages(rendered)
      } catch (e) {
        setError('Falha ao carregar documento.')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id])

  // Draw rendered pages onto DOM canvases
  useEffect(() => {
    pages.forEach((p, i) => {
      const el = canvasRefs.current[i]
      if (!el) return
      el.width = p.canvas.width
      el.height = p.canvas.height
      el.getContext('2d')!.drawImage(p.canvas, 0, 0)
    })
  }, [pages])

  const handleMouseMove = useCallback((e: MouseEvent) => {
    const d = dragRef.current
    if (!d) return
    const dx = (e.clientX - d.startMouseX) / SCALE
    const dy = (e.clientY - d.startMouseY) / SCALE
    if (d.type === 'move') {
      setFields((prev) => prev.map((f, i) => i === d.fieldIdx
        ? { ...f, x: Math.round(d.startFieldX + dx), y: Math.round(d.startFieldY + dy) }
        : f))
    } else {
      setFields((prev) => prev.map((f, i) => i === d.fieldIdx
        ? { ...f, width: Math.round(Math.max(20, d.startW + dx)), height: Math.round(Math.max(10, d.startH + dy)) }
        : f))
    }
  }, [])

  const handleMouseUp = useCallback(() => { dragRef.current = null }, [])

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [handleMouseMove, handleMouseUp])

  function startDrag(e: React.MouseEvent, fieldIdx: number, type: 'move' | 'resize') {
    e.stopPropagation()
    e.preventDefault()
    const f = fields[fieldIdx]
    dragRef.current = {
      type, fieldIdx,
      startMouseX: e.clientX, startMouseY: e.clientY,
      startFieldX: f.x, startFieldY: f.y,
      startW: f.width, startH: f.height,
    }
    setSelectedFieldIdx(fieldIdx)
    setActiveTool(null)
  }

  function handlePageClick(e: React.MouseEvent<HTMLDivElement>, pageIndex: number) {
    if (!activeTool) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = Math.round((e.clientX - rect.left) / SCALE)
    const y = Math.round((e.clientY - rect.top) / SCALE)
    const def = FIELD_DEFAULTS[activeTool] ?? { width: 200, height: 60 }
    const newField: FieldForm = {
      tipo: activeTool,
      signerId: activeSignerId,
      page: pageIndex + 1,
      x,
      y,
      width: def.width,
      height: def.height,
      required: true,
    }
    const newFields = [...fields, newField]
    setFields(newFields)
    setSelectedFieldIdx(newFields.length - 1)
    setActiveTool(null) // one-shot placement
  }

  function updateField(index: number, updates: Partial<FieldForm>) {
    setFields((prev) => prev.map((f, i) => i === index ? { ...f, ...updates } : f))
  }

  function removeField(index: number) {
    setFields((prev) => prev.filter((_, i) => i !== index))
    setSelectedFieldIdx(null)
  }

  async function handleSave() {
    setError(''); setSuccess(''); setSaving(true)
    try {
      await api.post(`/api/documents/${id}/fields`, { fields })
      setSuccess('Campos salvos.')
    } catch { setError('Falha ao salvar campos.') }
    finally { setSaving(false) }
  }

  async function handleSend() {
    setError(''); setSuccess(''); setSending(true)
    try {
      await api.post(`/api/documents/${id}/send`)
      setSuccess('Documento enviado para assinatura!')
      setTimeout(() => navigate(`/documents/${id}`), 1500)
    } catch { setError('Falha ao enviar documento.') }
    finally { setSending(false) }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
      </div>
    )
  }

  if (!doc) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Documento não encontrado. <Link to="/dashboard" className="text-indigo-600">Voltar</Link></p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="px-4 flex items-center justify-between h-14">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-indigo-600 hover:text-indigo-800 text-sm font-medium">
              &larr; {t('common.back', 'Voltar')}
            </Link>
            <span className="text-gray-900 font-semibold truncate max-w-xs">{doc.nome}</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-1.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 transition">
              {saving ? 'Salvando...' : 'Salvar campos'}
            </button>
            <button onClick={handleSend} disabled={sending || fields.length === 0}
              className="px-4 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition">
              {sending ? 'Enviando...' : 'Enviar para assinar'}
            </button>
          </div>
        </div>
      </header>

      {(error || success) && (
        <div className="px-4 pt-3">
          {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm mb-2">{error}</div>}
          {success && <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm mb-2">{success}</div>}
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        {/* PDF canvas area */}
        <div className="flex-1 overflow-auto bg-gray-100 p-6">
          {activeTool && (
            <div className="mb-3 flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2 text-sm text-indigo-700 font-medium">
              Clique no PDF para posicionar o campo <strong>{activeTool}</strong>
              <button onClick={() => setActiveTool(null)} className="ml-auto text-indigo-400 hover:text-indigo-700 text-xs underline">cancelar</button>
            </div>
          )}

          {pages.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-gray-400">Carregando PDF...</div>
          ) : (
            <div className="flex flex-col gap-6 items-center">
              {pages.map((p, pageIndex) => {
                const pageFields = fields
                  .map((f, i) => ({ ...f, _i: i }))
                  .filter((f) => f.page === pageIndex + 1)

                return (
                  <div key={pageIndex}>
                    <div className="text-xs text-gray-400 text-center mb-1">Página {pageIndex + 1}</div>
                    <div
                      className="relative shadow-lg"
                      style={{ width: p.canvas.width, height: p.canvas.height, cursor: activeTool ? 'crosshair' : 'default' }}
                      onClick={(e) => handlePageClick(e, pageIndex)}
                    >
                      {/* PDF rendered canvas */}
                      <canvas
                        ref={(el) => { canvasRefs.current[pageIndex] = el }}
                        style={{ display: 'block', pointerEvents: 'none' }}
                      />

                      {/* Field boxes */}
                      {pageFields.map((f) => {
                        const color = FIELD_COLORS[f.tipo] ?? { bg: 'rgba(99,102,241,0.2)', border: '#6366F1' }
                        const isSelected = selectedFieldIdx === f._i
                        return (
                          <div
                            key={f._i}
                            onMouseDown={(e) => startDrag(e, f._i, 'move')}
                            onClick={(e) => { e.stopPropagation(); setSelectedFieldIdx(f._i); setActiveTool(null) }}
                            style={{
                              position: 'absolute',
                              left: f.x * SCALE,
                              top: f.y * SCALE,
                              width: f.width * SCALE,
                              height: f.height * SCALE,
                              backgroundColor: color.bg,
                              border: `2px solid ${color.border}`,
                              outline: isSelected ? `3px solid ${color.border}` : undefined,
                              outlineOffset: 2,
                              borderRadius: 3,
                              cursor: 'move',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 10,
                              fontWeight: 700,
                              color: color.border,
                              userSelect: 'none',
                              zIndex: 10,
                            }}
                          >
                            {f.tipo}
                            {/* Resize handle — bottom-right corner */}
                            <div
                              onMouseDown={(e) => startDrag(e, f._i, 'resize')}
                              style={{
                                position: 'absolute',
                                right: 0, bottom: 0,
                                width: 10, height: 10,
                                backgroundColor: color.border,
                                cursor: 'se-resize',
                                borderRadius: '2px 0 2px 0',
                              }}
                            />
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="w-72 bg-white border-l border-gray-100 flex flex-col overflow-hidden">
          {/* Tool selector */}
          <div className="p-4 border-b border-gray-100">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              {activeTool ? `Posicionando: ${activeTool}` : 'Clique para posicionar'}
            </div>
            {/* Signer selector for placement */}
            <select
              value={activeSignerId}
              onChange={(e) => setActiveSignerId(e.target.value)}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg mb-3 focus:ring-1 focus:ring-indigo-500 outline-none"
            >
              <option value="">Signatário (qualquer)</option>
              {doc.signers?.map((s) => (
                <option key={s.id} value={s.id}>{s.nome}</option>
              ))}
            </select>
            <div className="grid grid-cols-2 gap-1.5">
              {FIELD_TYPES.map((ft) => (
                <button
                  key={ft.tipo}
                  onClick={() => setActiveTool(activeTool === ft.tipo ? null : ft.tipo)}
                  className={`px-2 py-2 text-xs font-semibold rounded-lg border transition ${
                    activeTool === ft.tipo
                      ? 'bg-indigo-600 border-indigo-600 text-white'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-indigo-300 hover:text-indigo-600'
                  }`}
                >
                  {ft.label}
                </button>
              ))}
            </div>
          </div>

          {/* Field list */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Campos ({fields.length})
            </div>
            {fields.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                Selecione um tipo e clique no PDF para adicionar campos
              </p>
            ) : (
              <div className="space-y-2">
                {fields.map((field, index) => {
                  const color = FIELD_COLORS[field.tipo] ?? { bg: 'rgba(99,102,241,0.2)', border: '#6366F1' }
                  const isSelected = selectedFieldIdx === index
                  return (
                    <div
                      key={index}
                      onClick={() => setSelectedFieldIdx(isSelected ? null : index)}
                      style={{ borderColor: isSelected ? color.border : undefined }}
                      className={`border rounded-lg p-3 cursor-pointer transition ${
                        isSelected ? 'border-2 bg-gray-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-bold" style={{ color: color.border }}>{field.tipo}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); removeField(index) }}
                          className="text-xs text-red-400 hover:text-red-600"
                        >
                          Remover
                        </button>
                      </div>

                      {isSelected && (
                        <div className="space-y-2 mt-2">
                          <div>
                            <label className="block text-xs text-gray-500 mb-0.5">Signatário</label>
                            <select
                              value={field.signerId}
                              onChange={(e) => updateField(index, { signerId: e.target.value })}
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 outline-none"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <option value="">Qualquer</option>
                              {doc.signers?.map((s) => (
                                <option key={s.id} value={s.id}>{s.nome}</option>
                              ))}
                            </select>
                          </div>
                          <div className="grid grid-cols-3 gap-1">
                            {(['page', 'x', 'y'] as const).map((k) => (
                              <div key={k}>
                                <label className="block text-xs text-gray-500 mb-0.5 capitalize">{k}</label>
                                <input type="number" min={k === 'page' ? 1 : 0} value={field[k]}
                                  onChange={(e) => updateField(index, { [k]: parseInt(e.target.value) || 0 })}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-full px-1.5 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 outline-none" />
                              </div>
                            ))}
                          </div>
                          <div className="grid grid-cols-2 gap-1">
                            {(['width', 'height'] as const).map((k) => (
                              <div key={k}>
                                <label className="block text-xs text-gray-500 mb-0.5 capitalize">{k === 'width' ? 'Largura' : 'Altura'}</label>
                                <input type="number" min={10} value={field[k]}
                                  onChange={(e) => updateField(index, { [k]: parseInt(e.target.value) || 50 })}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-full px-1.5 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 outline-none" />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {!isSelected && (
                        <p className="text-xs text-gray-400">
                          Pág. {field.page} · ({field.x}, {field.y})
                          {field.signerId && doc.signers?.find(s => s.id === field.signerId)
                            ? ` · ${doc.signers.find(s => s.id === field.signerId)!.nome.split(' ')[0]}`
                            : ''}
                        </p>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
